/**
 * The transcript. Keyed by real message id (never by array index — bubbles
 * gain their ULID mid-stream and get re-keyed) and rendered in the order the
 * server gave us: `seq` order, never sorted by `id`.
 *
 * Two scroll behaviours meet here:
 *  • follow the stream, but only while the member is already at the bottom —
 *    the reply really does arrive token by token, so pinning unconditionally
 *    would yank the view every ~30ms and make reading back impossible;
 *  • scrolling to the top pages the transcript backwards, holding the reading
 *    position steady while older turns are inserted above it.
 */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { ExclamationTriangleIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import AttachmentChip from '@/components/chat/AttachmentChip';
import { useAssistant, type TeriMessage } from '@/contexts/AssistantContext';
import type { AssistantAttachment } from '@/types/assistant';

const TERI_AVATAR = '/tery/teri_ai_avatar_v2.png';

/** How close to the bottom still counts as "following the stream". */
const STICK_THRESHOLD = 48;
/** How close to the top starts loading the previous page. */
const LOAD_OLDER_THRESHOLD = 200;

const TeriAvatar: React.FC = () => (
  <span className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-white dark:bg-grey-800 ring-1 ring-black/[0.06] dark:ring-white/10">
    <img src={TERI_AVATAR} alt="" className="w-full h-full object-cover" />
  </span>
);

const MessageAttachments: React.FC<{
  attachments: AssistantAttachment[];
  previewUrls: Record<string, string>;
  /** Teri got filenames instead of pixels — say so on the image itself. */
  visionBlind: boolean;
}> = ({ attachments, previewUrls, visionBlind }) => (
  <div className="flex flex-wrap gap-1.5">
    {attachments.map((attachment) => {
      const preview = previewUrls[attachment.id];
      if (attachment.kind === 'image' && preview) {
        return (
          <figure key={attachment.id} className="relative">
            <img
              src={preview}
              alt={attachment.filename}
              className="max-h-40 max-w-full rounded-lg border border-border-secondary object-cover"
            />
            {visionBlind && (
              <figcaption className="mt-1 flex items-center gap-1 text-[10px] text-text-tertiary">
                <EyeSlashIcon className="w-3 h-3 shrink-0" />
                Sent as filename only — I can&apos;t see images yet
              </figcaption>
            )}
          </figure>
        );
      }
      return (
        <AttachmentChip
          key={attachment.id}
          name={attachment.filename}
          mime={attachment.mime_type}
          size={attachment.size}
          error={
            attachment.status === 'failed'
              ? (attachment.error_message ?? "Couldn't be read")
              : undefined
          }
        />
      );
    })}
  </div>
);

const MentionPills: React.FC<{ mentions: TeriMessage['mentions'] }> = ({ mentions }) => (
  <div className="flex flex-wrap gap-1">
    {mentions.map((mention) => (
      <span
        key={`${mention.type}:${mention.id}`}
        className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-350 text-[11px] font-semibold"
      >
        @{mention.label}
      </span>
    ))}
  </div>
);

const MessageRow: React.FC<{ message: TeriMessage; live: boolean }> = ({
  message,
  live,
}) => {
  const mine = message.from === 'me';
  // A stored `streaming` turn means the server died mid-answer; a *live* one
  // is simply still arriving. Same status, opposite meaning.
  const broken = message.status === 'failed' || (message.status === 'streaming' && !live);

  const bubbleTone = mine
    ? 'bg-primary-500 rounded-br-sm' // ink comes from the fill (white brand = black text)
    : broken
      ? 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 rounded-bl-sm border border-error-600/20'
      : 'bg-white dark:bg-grey-800 text-text-primary rounded-bl-sm shadow-sm';

  return (
    <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <span className={`text-[11px] text-text-tertiary mb-1 ${mine ? 'mr-1' : 'ml-10'}`}>
        {message.at}
      </span>
      <div className="flex items-end gap-2 max-w-[92%]">
        {!mine && <TeriAvatar />}
        <div className={`min-w-0 flex flex-col gap-1.5 ${mine ? 'items-end' : 'items-start'}`}>
          {message.attachments.length > 0 && (
            <MessageAttachments
              attachments={message.attachments}
              previewUrls={message.previewUrls}
              visionBlind={message.visionBlind}
            />
          )}
          {message.mentions.length > 0 && <MentionPills mentions={message.mentions} />}
          {(message.text || !message.attachments.length) && (
            <p
              // Screen readers get the answer as it lands, not on next focus.
              aria-live={live ? 'polite' : undefined}
              className={`px-3.5 py-2.5 text-sm leading-relaxed rounded-xl whitespace-pre-wrap ${bubbleTone}`}
            >
              {message.text}
            </p>
          )}
        </div>
      </div>

      {/* Non-blocking notes: a mention that was dropped, a document that did
          not fit. They explain a gap in the answer without becoming a toast. */}
      {message.notes.length > 0 && (
        <ul className={`mt-1 space-y-0.5 max-w-[92%] ${mine ? 'mr-1' : 'ml-10'}`}>
          {message.notes.map((note) => (
            <li
              key={note}
              className="flex items-start gap-1 text-[11px] text-text-tertiary"
              data-testid="teri-message-note"
            >
              <ExclamationTriangleIcon className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AssistantMessageList: React.FC = () => {
  const { messages, thinking, hasMore, loadingOlder, loadOlder } = useAssistant();
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const heightBeforePrepend = useRef(0);

  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Sending is an explicit "take me to the newest" — re-arm the follow.
    if (lastMessage?.from === 'me') stickToBottom.current = true;
    if (stickToBottom.current && !heightBeforePrepend.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, lastMessage]);

  // Hold the reading position when a previous page lands above the viewport.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !heightBeforePrepend.current) return;
    const delta = el.scrollHeight - heightBeforePrepend.current;
    if (delta > 0) el.scrollTop += delta;
    heightBeforePrepend.current = 0;
  }, [messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD;
    if (el.scrollTop < LOAD_OLDER_THRESHOLD && hasMore && !loadingOlder) {
      heightBeforePrepend.current = el.scrollHeight;
      void loadOlder();
    }
  };

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto px-3.5 pt-4 pb-4 space-y-4 scrollbar-thin"
      data-testid="teri-messages"
    >
      {hasMore && (
        <p className="text-center text-[11px] text-text-tertiary">
          {loadingOlder ? 'Loading earlier messages…' : 'Scroll up for earlier messages'}
        </p>
      )}

      {messages.map((message, index) => (
        <MessageRow
          key={message.id}
          message={message}
          live={thinking && index === messages.length - 1 && message.from === 'teri'}
        />
      ))}

      {/* Thinking indicator lives at the BOTTOM of the thread — exactly where
          the answer will appear — and is replaced in place by the streaming
          reply bubble. */}
      {thinking && lastMessage?.from === 'me' && (
        <div className="flex items-end gap-2" data-testid="teri-thinking">
          <TeriAvatar />
          <span className="px-3.5 py-2.5 rounded-xl rounded-bl-sm bg-white dark:bg-grey-800 text-text-tertiary text-sm animate-pulse">
            Teri is thinking…
          </span>
        </div>
      )}
    </div>
  );
};

export default AssistantMessageList;
